import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

const WISTIA_PLAYER_URL = "https://fast.wistia.com/assets/external/E-v1.js";
const WISTIA_ELEMENTS = ".wistia_embed, [data-wistia-link]";

let _wistiaLoading: Promise<unknown> | null = null;

// Load the Js library for the Wistia video player
export const loadWistiaPlayer = async (): Promise<unknown> => {
    const document = getDocument();
    if (!document) {
        return Promise.reject(new Error("Document not available"));
    }
    if (!_wistiaLoading) {
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = WISTIA_PLAYER_URL;
        _wistiaLoading = new Promise((resolve, reject) => {
            s.onload = resolve;
            s.onerror = reject;
        });
        document.body.appendChild(s);
    }
    return _wistiaLoading;
};

const holdsWistiaElement = (node: Node): boolean =>
    node instanceof Element &&
    (node.matches(WISTIA_ELEMENTS) || !!node.querySelector(WISTIA_ELEMENTS));

/**
 * Loads the Wistia player as soon as the page holds a Wistia embed or popover
 * link, whether it is there at hydration or arrives later (UGC modals, lazily
 * rendered blocks). CMS HTML reaches the page without Wistia's own script
 * tags, so this is what makes those embeds play. Returns a cleanup function.
 */
export const watchForWistia = (): (() => void) => {
    const document = getDocument();
    if (!document) {
        return () => {};
    }
    const load = () => {
        void loadWistiaPlayer().catch(() => {});
    };
    if (document.querySelector(WISTIA_ELEMENTS)) {
        load();
        return () => {};
    }
    const observer = new MutationObserver((mutations) => {
        const added = mutations.some((mutation) =>
            Array.from(mutation.addedNodes).some(holdsWistiaElement),
        );
        if (added) {
            observer.disconnect();
            load();
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
};
